import React, { useEffect, useState } from 'react';
import { Loader2, ArrowRight, CreditCard, Wallet, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const todayStr = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

/** Changement d'horaire côté client : choix d'un créneau, devis (même prix / complément / avoir), confirmation. */
export default function RescheduleTeeTime({ booking, token, settings, onBack, onDone }) {
  const [date, setDate] = useState(booking.slot_date || todayStr());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [closed, setClosed] = useState(false);
  const [picked, setPicked] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [busy, setBusy] = useState(false);
  const maxDate = plusDays(Number(settings?.booking_open_days_ahead || 14));

  useEffect(() => {
    if (!date) return;
    setLoadingSlots(true); setPicked(null); setQuote(null); setQuoteError('');
    fetch(`${API_URL}/api/tee-times/portal/slots/${date}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Erreur'); return d; })
      .then((d) => { setClosed(!!d.closed); setSlots(d.slots || []); })
      .catch((e) => { setSlots([]); setClosed(false); toast.error(e.message); })
      .finally(() => setLoadingSlots(false));
  }, [date, token]);

  const pick = async (time) => {
    setPicked(time); setQuote(null); setQuoteError('');
    try {
      const res = await fetch(`${API_URL}/api/tee-times/portal/bookings/${booking.id}/reschedule-quote?slot_date=${date}&slot_time=${time}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Créneau indisponible');
      setQuote(d);
    } catch (e) {
      setQuoteError(e.message);
    }
  };

  const confirm = async () => {
    if (!quote) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/tee-times/portal/bookings/${booking.id}/reschedule`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot_date: date, slot_time: picked }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      if (d.checkout_url) { window.location.href = d.checkout_url; return; }
      if (d.adjustment?.kind === 'credit') toast.success(`Départ déplacé — ${Number(d.adjustment.amount).toFixed(2)} € crédités sur votre compte`);
      else toast.success(`Départ déplacé au ${date} ${picked}`);
      onDone?.(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const available = slots.filter((s) => s.can_book && s.available_spots >= (booking.players_count || 1) && !(date === booking.slot_date && s.time === booking.slot_time));

  return (
    <div className="space-y-3" data-testid="reschedule-panel">
      <div className="bg-sky-50 border border-sky-200 rounded p-2 text-[11px] text-sky-800">
        Actuellement : <strong>{booking.slot_date} à {booking.slot_time}</strong>. Choisissez un nouveau créneau — si le tarif est différent, la différence est réglée en ligne ou créditée sur votre compte.
      </div>
      <div>
        <label className="text-xs font-semibold text-neutral-700">Nouvelle date</label>
        <input type="date" value={date} min={todayStr()} max={maxDate} onChange={(e) => setDate(e.target.value)} className="w-full h-9 text-sm border rounded px-2 mt-1" data-testid="reschedule-date" />
      </div>
      {loadingSlots ? (
        <div className="flex items-center gap-2 text-xs text-neutral-500 py-3"><Loader2 size={13} className="animate-spin" /> Recherche des créneaux…</div>
      ) : closed ? (
        <p className="text-xs text-neutral-500">Parcours fermé ce jour.</p>
      ) : available.length === 0 ? (
        <p className="text-xs text-neutral-500" data-testid="reschedule-no-slots">Aucun créneau disponible pour {booking.players_count || 1} joueur{(booking.players_count || 1) > 1 ? 's' : ''} ce jour.</p>
      ) : (
        <div className="grid grid-cols-4 gap-1.5 max-h-44 overflow-y-auto" data-testid="reschedule-slots">
          {available.map((s) => (
            <button
              key={s.time}
              type="button"
              onClick={() => pick(s.time)}
              className={`h-9 rounded-lg border text-xs font-semibold transition-colors ${picked === s.time ? 'text-white border-transparent' : 'border-neutral-200 text-neutral-700 hover:bg-neutral-50'}`}
              style={picked === s.time ? { backgroundColor: '#2E6A4C', color: '#fff' } : {}}
              data-testid={`reschedule-slot-${s.time}`}
            >
              {s.time}
            </button>
          ))}
        </div>
      )}

      {picked && quoteError && <p className="text-xs text-red-600" data-testid="reschedule-quote-error">{quoteError}</p>}
      {picked && !quote && !quoteError && <div className="flex items-center gap-2 text-xs text-neutral-500"><Loader2 size={12} className="animate-spin" /> Calcul du tarif…</div>}
      {quote && (
        <div className="rounded-xl border p-3 text-xs space-y-1" data-testid="reschedule-quote">
          <div className="flex items-center gap-1 font-semibold text-neutral-800">
            {booking.slot_date} {booking.slot_time} <ArrowRight size={12} /> {quote.slot_date} {quote.slot_time}
          </div>
          {quote.action === 'move' && <p className="text-emerald-700 flex items-center gap-1"><Check size={12} /> Même tarif — aucun paiement supplémentaire.</p>}
          {quote.action === 'pay' && <p className="text-indigo-700 flex items-center gap-1"><CreditCard size={12} /> Ce créneau est plus cher : complément de <strong>{Number(quote.diff).toFixed(2)} €</strong> à régler en ligne.</p>}
          {quote.action === 'credit' && <p className="text-emerald-700 flex items-center gap-1"><Wallet size={12} /> Ce créneau est moins cher : <strong>{Math.abs(Number(quote.diff)).toFixed(2)} €</strong> seront crédités sur votre compte client.</p>}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={confirm}
          disabled={!quote || busy}
          className="flex-1 h-10 rounded text-xs font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-1"
          style={{ backgroundColor: '#2E6A4C' }}
          data-testid="reschedule-confirm"
        >
          {busy && <Loader2 size={13} className="animate-spin" />}
          {quote?.action === 'pay' ? `Payer ${Number(quote.diff).toFixed(2)} € et déplacer` : 'Confirmer le nouvel horaire'}
        </button>
        <button onClick={onBack} className="px-3 text-xs text-neutral-500 underline" data-testid="reschedule-back">Retour</button>
      </div>
    </div>
  );
}
